import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { List } from 'lucide-react';

export const ArticleTableOfContents = ({ sections, mobileOnly = false }) => {
  const [activeId, setActiveId] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
    );

    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  if (!sections || sections.length < 3) return null;

  return (
    <>
      {/* Desktop sticky sidebar */}
      {!mobileOnly && (
      <nav className="hidden lg:block sticky top-24 self-start" data-testid="article-toc-desktop">
        <p className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-3">On this page</p>
        <ul className="space-y-1 border-l border-stone-200">
          {sections.map(s => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className={cn(
                  "block text-sm pl-4 py-1.5 border-l-2 -ml-px transition-colors",
                  activeId === s.id
                    ? "border-primary text-primary font-medium"
                    : "border-transparent text-stone-500 hover:text-stone-800 hover:border-stone-300"
                )}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      )}

      {/* Mobile floating button */}
      <div className="lg:hidden fixed bottom-4 right-4 z-40" data-testid="article-toc-mobile">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-12 h-12 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors"
        >
          <List className="w-5 h-5" />
        </button>
        {isOpen && (
          <div className="absolute bottom-14 right-0 w-64 bg-white rounded-lg shadow-xl border border-stone-200 p-3 max-h-[60vh] overflow-y-auto">
            <p className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-2 px-2">Sections</p>
            <ul className="space-y-0.5">
              {sections.map(s => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      setIsOpen(false);
                    }}
                    className={cn(
                      "block text-sm px-3 py-2 rounded-md transition-colors",
                      activeId === s.id
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-stone-600 hover:bg-stone-50"
                    )}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};
